import {
  designCardListSchema,
  designCardSchema,
  type CreateDesignCardInput,
  type DesignBoardOwner,
  type DesignCard,
  type DesignCardList,
  type ListDesignCardsQuery,
  type UpdateDesignCardInput,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';

/**
 * Every design-card call the app makes.
 *
 * THE OWNER IS ALWAYS IN THE URL, never in a body a client could point at somebody else's board —
 * `createDesignCardSchema` leaves it out for exactly that reason. A board belongs to an order or a
 * customer, and the path segment is the only thing that differs between them.
 *
 * Editing and deleting go to the card's OWN address, because a card's id identifies it and an
 * editor should not have to know which of the two boards it is on.
 */

/** The path segment a board's owner reads as: `/orders/:id` or `/customers/:id`. */
function ownerPath(owner: DesignBoardOwner): string {
  switch (owner.kind) {
    case 'order':
      return `/orders/${owner.orderId}`;
    default:
      return `/customers/${owner.customerId}`;
  }
}

/** Unpaginated, like the gallery's folders: a board has a handful of cards, not pages of them. */
export async function listDesignCards(
  client: ApiClient,
  owner: DesignBoardOwner,
  query: ListDesignCardsQuery = {},
): Promise<DesignCardList> {
  return await client.get(`${ownerPath(owner)}/design-cards`, {
    schema: designCardListSchema,
    query: {
      ...(query.orderItemId ? { orderItemId: query.orderItemId } : {}),
      ...(query.status ? { status: query.status } : {}),
    },
  });
}

export async function createDesignCard(
  client: ApiClient,
  owner: DesignBoardOwner,
  input: CreateDesignCardInput,
): Promise<DesignCard> {
  return await client.post(`${ownerPath(owner)}/design-cards`, input, {
    schema: designCardSchema,
  });
}

export async function updateDesignCard(
  client: ApiClient,
  cardId: string,
  patch: UpdateDesignCardInput,
): Promise<DesignCard> {
  return await client.patch(`/design-cards/${cardId}`, patch, { schema: designCardSchema });
}

export async function deleteDesignCard(client: ApiClient, cardId: string): Promise<void> {
  await client.del(`/design-cards/${cardId}`);
}
